import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft, faPrint, faCircleCheck, faMinus } from '@fortawesome/free-solid-svg-icons';
import { useAuth } from '../context/AuthContext';
import { getElection } from '../services/api';
import './ElectionReport.css';

const ElectionReport = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isAdmin, isSecretary } = useAuth();
  const [election, setElection] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!isAdmin && !isSecretary) { navigate('/'); return; }
    (async () => {
      try {
        const { data } = await getElection(id);
        setElection(data.election);
      } catch (e) { /* noop */ }
      setLoading(false);
    })();
  }, [id, isAdmin, isSecretary, navigate]);

  if (loading) return <div className="loading">Carregando...</div>;
  if (!election) return null;

  const decided = election.ministries.filter(m => m.status === 'decided');
  // ministérios com várias vagas ganham um bloco detalhado à parte
  const multiSeatMinistries = election.ministries.filter(m => (m.seats || 1) > 1 && ((m.electedList || []).length > 0 || (m.rejectedList || []).length > 0));
  // Cargos escolhidos entre os eleitos (ex.: Primeiro Ancião) de um ministério
  const derivedWinners = (parent) => election.ministries
    .filter(d => d.parentMinistryId === parent._id && d.electedName)
    .map(d => ({ role: d.name, name: d.electedName }));

  return (
    <div className="report-page">
      <div className="report-toolbar no-print">
        <button className="btn btn-secondary" onClick={() => navigate(`/admin/votacoes/${id}`)}>
          <FontAwesomeIcon icon={faArrowLeft} /> Voltar
        </button>
        <button className="btn btn-primary" onClick={() => window.print()}>
          <FontAwesomeIcon icon={faPrint} /> Imprimir / PDF
        </button>
      </div>

      <div className="report-sheet">
        <h1>{election.title}</h1>
        <p className="report-sub">Relatório de Nomeações — {election.year}</p>
        <p className="report-meta">
          {decided.length} de {election.ministries.length} ministérios com líder eleito
        </p>

        <table className="report-table">
          <thead>
            <tr>
              <th>Ministério</th>
              <th>Eleito</th>
              <th>Votos</th>
              <th>%</th>
              <th>Rodadas</th>
              <th>Origem</th>
            </tr>
          </thead>
          <tbody>
            {election.ministries.map((m) => {
              const validRounds = (m.rounds || []).filter(r => !r.voided && r.status === 'closed');
              const multiSeat = (m.seats || 1) > 1;
              const winnerRound = validRounds.find(r => r.conclusive);
              const winnerRes = winnerRound && winnerRound.results.find(r => r.name === winnerRound.winnerName);
              const electedCount = (m.electedList || []).length;
              return (
                <tr key={m._id} className={`${m.status === 'decided' ? 'row-decided' : ''} ${m.parentMinistryId ? 'row-derived' : ''}`}>
                  <td>
                    {m.parentMinistryId && <span className="report-derived-mark">↳ </span>}
                    {m.name}{multiSeat && <span className="report-seats"> ({m.seats} vagas)</span>}
                  </td>
                  <td>
                    {m.electedName
                      ? <><FontAwesomeIcon icon={faCircleCheck} className="tw" /> {m.electedName}</>
                      : <span className="muted"><FontAwesomeIcon icon={faMinus} /> não definido</span>}
                  </td>
                  <td>{multiSeat ? `${electedCount}/${m.seats}` : (winnerRes ? winnerRes.votes : (m.electedSource === 'manual' ? '—' : ''))}</td>
                  <td>{multiSeat ? '—' : (winnerRes ? `${winnerRes.percent}%` : (m.electedSource === 'manual' ? '—' : ''))}</td>
                  <td>{validRounds.length}</td>
                  <td>{m.electedSource === 'manual' ? 'Manual' : (m.electedSource === 'vote' ? 'Votação' : '—')}</td>
                </tr>
              );
            })}
          </tbody>
        </table>

        {multiSeatMinistries.map((m) => (
          <div key={m._id} className="report-multiseat">
            <h2 className="report-multiseat-title">{m.name} — {m.seats} vagas</h2>
            {(m.electedList || []).length > 0 && (
              <table className="report-table">
                <thead>
                  <tr><th>Eleito</th><th>Rodada</th><th>Votos</th><th>%</th></tr>
                </thead>
                <tbody>
                  {m.electedList.map((e, i) => (
                    <tr key={i}>
                      <td>
                        <FontAwesomeIcon icon={faCircleCheck} className="tw" /> {e.name}
                        {derivedWinners(m).filter(d => d.name === e.name).map(d => (
                          <span key={d.role} className="report-role"> — {d.role}</span>
                        ))}
                      </td>
                      <td>{e.round || '—'}</td>
                      <td>{e.votes || '—'}</td>
                      <td>{e.percent ? `${e.percent}%` : '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
            {(m.rejectedList || []).length > 0 && (
              <>
                <p className="report-rejected-label">Candidatos rejeitados</p>
                <table className="report-table report-table-rejected">
                  <thead>
                    <tr><th>Nome</th><th>Rodada</th><th>Votos</th><th>Motivo</th></tr>
                  </thead>
                  <tbody>
                    {m.rejectedList.map((r, i) => (
                      <tr key={i}>
                        <td>{r.name}</td>
                        <td>{r.round || '—'}</td>
                        <td>{r.votes || '—'}</td>
                        <td>{r.reason || '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </>
            )}
          </div>
        ))}

        <div className="report-signatures">
          <div><span className="sig-line" />Secretário(a)</div>
          <div><span className="sig-line" />Presidente da Comissão</div>
        </div>
      </div>
    </div>
  );
};

export default ElectionReport;
